import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FolderKanban, Wand2, Image as ImageIcon, Video, CalendarClock,
  Sparkles, ArrowLeft, Library, Target, BellRing, Wallet, X, Clock, CreditCard, Gauge, Gift,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useProjects } from "@/context/ProjectContext";
import ReviewWidget from "@/components/ReviewWidget";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-6 border border-border card-hover">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="text-3xl font-extrabold">{value}</div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
  </motion.div>
);

const BalanceCard = ({ icon: Icon, label, used = 0, limit = -1, color }) => {
  const unlimited = limit === -1;
  const remaining = unlimited ? null : Math.max(0, limit - used);
  const pct = unlimited || !limit ? 0 : Math.min(100, (used / limit) * 100);
  return (
    <div className="rounded-xl p-4 border border-border bg-secondary/40" data-testid={`balance-${label}`}>
      <div className={`flex items-center gap-2 mb-1 ${color}`}><Icon className="w-4 h-4" /> <span className="text-sm font-bold">{label}</span></div>
      <div className="text-2xl font-extrabold">
        {unlimited ? "∞" : remaining}
        {!unlimited && <span className="text-xs text-muted-foreground"> متبقٍّ</span>}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{unlimited ? "غير محدود" : `مستخدم ${used} من ${limit}`}</div>
      {!unlimited && (
        <div className="h-1.5 rounded-full bg-background mt-2 overflow-hidden">
          <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

const entries = [
  { to: "/app/studio?individual=1", icon: Wand2, label: "توليد فردي", desc: "صورة، شعار، بطاقة عمل، بروشور أو فيديو — خدمات حسب الطلب بدون اشتراك من 8 ريال", color: "text-primary" },
  { track: "strategy", icon: Sparkles, label: "مشروع استراتيجية", desc: "رحلة كاملة · 499 / 1,299 ريال", color: "text-accent" },
  { track: "content", icon: FolderKanban, label: "مشروع محتوى", desc: "توليد للمشروع · 249 ريال/شهر", color: "text-emerald-400" },
  { to: "/app/packages", icon: CreditCard, label: "الباقات والدفع", desc: "اشترك أو اشحن محفظتك", color: "text-violet-400" },
];

export function Reminders() {
  const { selectProject } = useProjects();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  useEffect(() => { api.get("/reminders").then((r) => setReminders(r.data.reminders || [])).catch(() => {}); }, []);
  const goCycle = (projectId) => { if (projectId) selectProject(projectId); navigate("/app/goals"); };
  const snooze = async (projectId, days) => {
    try { await api.post(`/reminders/${projectId}/snooze`, { days }); setReminders((r) => r.filter((x) => x.project_id !== projectId)); } catch (e) {}
  };
  if (!reminders.length) return null;
  return (
    <div className="space-y-2" data-testid="reminders">
      {reminders.map((r) => (
        <div key={r.project_id} className="glass rounded-2xl p-3 border border-accent/40 bg-accent/5 flex items-center justify-between gap-3 flex-wrap" data-testid={`reminder-${r.project_id}`}>
          <div className="flex items-center gap-2 text-sm"><BellRing className="w-4 h-4 text-accent" /><span className="font-bold">{r.project_name}</span><span className="text-muted-foreground">{r.message}</span></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => goCycle(r.project_id)} className="bg-accent hover:bg-accent/90 text-white h-8" data-testid={`reminder-go-${r.project_id}`}>ابدأ الدورة</Button>
            <Button size="sm" variant="outline" onClick={() => snooze(r.project_id, 7)} className="border-border h-8" data-testid={`reminder-snooze-${r.project_id}`}>لاحقاً</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DashboardPanels() {
  const { activeProjects: projects, selectProject } = useProjects();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ projects: 0, strategies: 0, content: 0, campaigns: 0, images: 0, videos: 0 });
  const [reminders, setReminders] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/reminders").then((r) => setReminders(r.data.reminders || [])).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, [projects]);

  const goCycle = (projectId) => {
    if (projectId) selectProject(projectId);
    navigate("/app/goals");
  };

  const snooze = async (projectId, days) => {
    try {
      await api.post(`/reminders/${projectId}/snooze`, { days });
      setReminders((r) => r.filter((x) => x.project_id !== projectId));
      toast.success(days >= 365 ? "تم إيقاف التذكير" : `تم تأجيل التذكير ${days} أيام`);
    } catch (e) {
      toast.error("تعذّر تأجيل التذكير");
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-panels">
      {reminders.length > 0 && (
        <div className="space-y-2" data-testid="reminders">
          {reminders.map((r) => (
            <div key={r.project_id} className="glass rounded-2xl p-4 border border-accent/40 bg-accent/5 flex items-center justify-between gap-4 flex-wrap" data-testid={`reminder-${r.project_id}`}>
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-accent shrink-0" />
                <p className="text-sm"><b>{r.project_name}</b>: {r.message}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => goCycle(r.project_id)} className="text-sm font-bold text-accent hover:underline" data-testid={`reminder-cta-${r.project_id}`}>
                  ابدأ الدورة الجديدة ←
                </button>
                <button onClick={() => snooze(r.project_id, 3)} className="text-xs flex items-center gap-1 bg-secondary hover:bg-secondary/70 px-2.5 py-1 rounded-full text-muted-foreground" data-testid={`snooze-3-${r.project_id}`}>
                  <Clock className="w-3 h-3" /> 3 أيام
                </button>
                <button onClick={() => snooze(r.project_id, 7)} className="text-xs flex items-center gap-1 bg-secondary hover:bg-secondary/70 px-2.5 py-1 rounded-full text-muted-foreground" data-testid={`snooze-7-${r.project_id}`}>
                  <Clock className="w-3 h-3" /> أسبوع
                </button>
                <button onClick={() => snooze(r.project_id, 365)} title="إيقاف التذكير" className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary hover:bg-destructive/20 hover:text-destructive text-muted-foreground" data-testid={`dismiss-${r.project_id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={FolderKanban} label="المشاريع" value={stats.projects} color="bg-primary/15 text-primary" />
        <StatCard icon={Target} label="الاستراتيجيات" value={stats.strategies} color="bg-fuchsia-500/15 text-fuchsia-400" />
        <StatCard icon={ImageIcon} label="الصور المولّدة" value={stats.images} color="bg-accent/15 text-accent" />
        <StatCard icon={Video} label="الفيديوهات" value={stats.videos} color="bg-sky-500/15 text-sky-400" />
        <StatCard icon={CalendarClock} label="الحملات" value={stats.campaigns} color="bg-emerald-500/15 text-emerald-400" />
        <StatCard icon={Library} label="إجمالي المحتوى" value={stats.content} color="bg-violet-500/15 text-violet-400" />
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">ابدأ من هنا</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {entries.map((q, i) => {
            const inner = (
              <>
                <q.icon className={`w-7 h-7 mb-3 ${q.color}`} />
                <div className="font-bold flex items-center gap-1">{q.label}
                  <ArrowLeft className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-xs text-muted-foreground mt-1">{q.desc}</div>
              </>
            );
            return q.track ? (
              <button key={i} type="button" onClick={() => navigate("/app/projects", { state: { create: q.track } })}
                data-testid={`entry-${q.track}`}
                className="text-start glass rounded-2xl p-5 border border-border card-hover group">
                {inner}
              </button>
            ) : (
              <Link key={i} to={q.to} data-testid={`entry-${i}`}
                className="glass rounded-2xl p-5 border border-border card-hover group">
                {inner}
              </Link>
            );
          })}
        </div>
      </div>

      {usage && (
        <div className="glass rounded-2xl p-6 border border-border" data-testid="balances-panel">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold">الأرصدة والمتبقّي</h2>
              <span className="text-xs bg-primary/15 text-primary px-2.5 py-1 rounded-full">باقة: {usage.plan?.name}</span>
              {(usage.grants?.videos > 0 || usage.grants?.images > 0) && (
                <span className="text-xs bg-accent/15 text-accent border border-accent/30 px-2.5 py-1 rounded-full font-bold flex items-center gap-1" data-testid="grant-balance">
                  <Gift className="w-3.5 h-3.5" /> رصيدك الإضافي: {usage.grants.videos_left} فيديو · {usage.grants.images_left} صورة
                </span>
              )}
            </div>
            <button onClick={() => navigate("/app/packages")} className="text-sm font-bold text-accent hover:underline flex items-center gap-1" data-testid="upgrade-link">
              ترقية الباقة / الدفع <ArrowLeft className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <BalanceCard icon={FolderKanban} label="المشاريع" used={usage.usage?.projects} limit={usage.plan?.limits?.projects} color="text-primary" />
            <BalanceCard icon={ImageIcon} label="الصور" used={usage.usage?.images} limit={usage.plan?.limits?.images} color="text-accent" />
            <BalanceCard icon={Video} label="الفيديو" used={usage.usage?.videos} limit={usage.plan?.limits?.videos} color="text-sky-400" />
            {(wallet?.pay_per_use || wallet?.overage_pay_per_use) ? (
              <div className="rounded-xl p-4 border border-accent/30 bg-accent/5" data-testid="balance-wallet">
                <div className="flex items-center gap-2 mb-1 text-accent"><Wallet className="w-4 h-4" /> <span className="text-sm font-bold">المحفظة</span></div>
                <div className="text-2xl font-extrabold text-accent">{wallet.balance} <span className="text-xs text-muted-foreground">ريال</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">للمحتوى الإضافي حسب الطلب</div>
              </div>
            ) : (
              <BalanceCard icon={Target} label="الاستراتيجيات" used={usage.usage?.strategies} limit={usage.plan?.limits?.strategies} color="text-fuchsia-400" />
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default function Dashboard() {
  return <div className="space-y-8" data-testid="dashboard-page"><DashboardPanels /><ReviewWidget /></div>;
}
